import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  useTheme,
  Menu,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  PlayArrow as ProcessIcon,
  CheckCircle as ApproveIcon,
  AttachMoney as FinalizeIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';

// Import design system components
import { Card, EmptyState, MagicLoader } from '../../design-system';

// Import components
import PageHeader from '../../components/ui/PageHeader';
import ConfirmDialog from '../../components/ui/ConfirmDialog';

// Import stores
import usePayrollStore from '../../stores/payrollStore';
import useNotificationStore from '../../stores/notificationStore';

const PayrollRuns = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    payrollRuns,
    fetchPayrollRuns,
    processPayroll,
    approvePayroll,
    finalizePayroll,
    isLoading,
    filters,
    setFilters,
  } = usePayrollStore();
  const { showSuccess, showError } = useNotificationStore();

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: '', run: null });
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch payroll runs on mount
  useEffect(() => {
    fetchPayrollRuns();
  }, [fetchPayrollRuns]);

  const handleMenuOpen = (event, run) => {
    setAnchorEl(event.currentTarget);
    setSelectedRun(run);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRun(null);
  };

  const handleAction = async (action) => {
    handleMenuClose();
    
    switch (action) {
      case 'view':
        navigate(`/payroll/runs/${selectedRun._id}`);
        break;
      case 'edit':
        navigate(`/payroll/runs/${selectedRun._id}/edit`);
        break;
      case 'process':
      case 'approve':
      case 'finalize':
        setConfirmDialog({ open: true, action, run: selectedRun });
        break;
      case 'export':
        // Export functionality would be implemented here
        showSuccess('Exporting payroll report...');
        break;
      default:
        break;
    }
  };

  const handleConfirmAction = async () => {
    const { action, run } = confirmDialog;
    setConfirmDialog({ open: false, action: '', run: null });

    try {
      switch (action) {
        case 'process':
          await processPayroll(run._id);
          showSuccess('Payroll processed successfully');
          break;
        case 'approve':
          await approvePayroll(run._id);
          showSuccess('Payroll approved successfully');
          break;
        case 'finalize':
          await finalizePayroll(run._id);
          showSuccess('Payroll finalized successfully');
          break;
      }
      fetchPayrollRuns();
    } catch (error) {
      showError(error.message || `Failed to ${action} payroll`);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft':
        return 'default';
      case 'processing':
        return 'info';
      case 'approved':
        return 'warning';
      case 'finalized':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getAvailableActions = (run) => {
    const actions = ['view'];
    
    switch (run.status) {
      case 'draft':
        actions.push('edit', 'process');
        break;
      case 'processing':
        actions.push('approve');
        break;
      case 'approved':
        actions.push('finalize');
        break;
      case 'finalized':
        actions.push('export');
        break;
    }
    
    return actions;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  };

  const filteredRuns = payrollRuns.filter(run => {
    const searchMatch = searchTerm === '' || 
      format(new Date(run.year, run.month - 1), 'MMMM yyyy').toLowerCase().includes(searchTerm.toLowerCase());
    
    const statusMatch = !filters.status || run.status === filters.status;
    
    return searchMatch && statusMatch;
  });

  if (isLoading && payrollRuns.length === 0) {
    return <MagicLoader />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Payroll Runs"
        subtitle="Manage monthly payroll processing"
        onBack={() => navigate('/payroll')}
        actions={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/payroll/runs/new')}
          >
            New Payroll Run
          </Button>
        }
      />

      <Card>
        {/* Filters */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search payroll runs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, maxWidth: 400 }}
            />
            
            <Button
              size="small"
              startIcon={<FilterIcon />}
              onClick={() => {/* Filter menu implementation */}}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {filteredRuns.length === 0 ? (
          <Box sx={{ p: 4 }}>
            <EmptyState
              title="No payroll runs found"
              description={searchTerm ? "Try adjusting your search" : "Create your first payroll run to get started"}
              action={
                !searchTerm && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/payroll/runs/new')}
                  >
                    Create Payroll Run
                  </Button>
                )
              }
            />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Date Range</TableCell>
                  <TableCell align="center">Employees</TableCell>
                  <TableCell align="right">Total Amount</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Created</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRuns.map((run) => (
                  <TableRow
                    key={run._id}
                    sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2">
                        {format(new Date(run.year, run.month - 1), 'MMMM yyyy')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {format(new Date(run.periodStart), 'MMM d')} - {format(new Date(run.periodEnd), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="center">{run.employeeCount}</TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle2">
                        {formatCurrency(run.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={run.status}
                        color={getStatusColor(run.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {format(new Date(run.createdAt), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, run)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedRun && getAvailableActions(selectedRun).map((action) => (
          <MenuItem key={action} onClick={() => handleAction(action)}>
            {action === 'view' && <><ViewIcon sx={{ mr: 1 }} /> View Details</>}
            {action === 'edit' && <><EditIcon sx={{ mr: 1 }} /> Edit</>}
            {action === 'process' && <><ProcessIcon sx={{ mr: 1 }} /> Process Payroll</>}
            {action === 'approve' && <><ApproveIcon sx={{ mr: 1 }} /> Approve</>}
            {action === 'finalize' && <><FinalizeIcon sx={{ mr: 1 }} /> Mark as Paid</>}
            {action === 'export' && <><DownloadIcon sx={{ mr: 1 }} /> Export Report</>}
          </MenuItem>
        ))}
      </Menu>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={`Confirm ${confirmDialog.action}`}
        content={`Are you sure you want to ${confirmDialog.action} the payroll for ${
          confirmDialog.run ? format(new Date(confirmDialog.run.year, confirmDialog.run.month - 1), 'MMMM yyyy') : ''
        }?`}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog({ open: false, action: '', run: null })}
      />
    </Box>
  );
};

export default PayrollRuns;